import React, { useEffect, useState, useRef, lazy } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence, useReducedMotion, useInView } from 'framer-motion';
import { ArrowRight, Link2Icon, PencilIcon, CheckIcon, X, Search, Sparkles, Send, Check } from 'lucide-react';
import { useCopy } from './copy/copyMode';
// Company names for the "Trusted by fast-moving teams" carousel — same set as
// the future homepage hero (MarketingHero), rendered as text logos.
const TRUST_LOGOS = ['Shopify', 'Notion', 'Ramp', 'Tonic', 'Linear', 'Ember', 'Stripe', 'Entropy Studios', 'Figma', 'Larger Caveman', 'Mercury', 'Vercel'];
// Tool logos for the hero "Giga plugs into your world" carousel — the apps
// Giga connects to, rendered as brand icons (simpleicons, with a graceful
// fallback if a slug is unavailable).
const TRUST_TOOL_LOGOS: {
  slug: string;
  name: string;
  light?: boolean;
}[] = [{
  slug: 'slack',
  name: 'Slack'
}, {
  slug: 'notion',
  name: 'Notion',
  light: true
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'stripe',
  name: 'Stripe'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'jira',
  name: 'Jira'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  light: true
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'github',
  name: 'GitHub',
  light: true
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'zapier',
  name: 'Zapier'
}, {
  slug: 'airtable',
  name: 'Airtable'
}];
// Reuse the integrations data from IntegrationsSection
const ALL_INTEGRATION_SLUGS: {
  slug: string;
  name: string;
  light?: boolean;
}[] = [{
  slug: 'slack',
  name: 'Slack'
}, {
  slug: 'github',
  name: 'GitHub',
  light: true
}, {
  slug: 'gitlab',
  name: 'GitLab'
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'notion',
  name: 'Notion',
  light: true
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'stripe',
  name: 'Stripe'
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  light: true
}, {
  slug: 'jira',
  name: 'Jira'
}, {
  slug: 'zoom',
  name: 'Zoom'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'dropbox',
  name: 'Dropbox'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'shopify',
  name: 'Shopify'
}, {
  slug: 'mailchimp',
  name: 'Mailchimp'
}, {
  slug: 'atlassian',
  name: 'Atlassian'
}, {
  slug: 'mondaydotcom',
  name: 'Monday'
}, {
  slug: 'confluence',
  name: 'Confluence',
  light: true
}, {
  slug: 'miro',
  name: 'Miro'
}, {
  slug: 'clickup',
  name: 'ClickUp'
}, {
  slug: 'zapier',
  name: 'Zapier'
}, {
  slug: 'intercom',
  name: 'Intercom'
}, {
  slug: 'airtable',
  name: 'Airtable'
}, {
  slug: 'trello',
  name: 'Trello'
}, {
  slug: 'calendly',
  name: 'Calendly'
}, {
  slug: 'loom',
  name: 'Loom'
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googlemeet',
  name: 'Google Meet'
}, {
  slug: 'googlesheets',
  name: 'Google Sheets'
}, {
  slug: 'googledocs',
  name: 'Google Docs',
  light: true
}, {
  slug: 'microsoftteams',
  name: 'Microsoft Teams'
}, {
  slug: 'microsoftoutlook',
  name: 'Outlook'
}, {
  slug: 'microsoftexcel',
  name: 'Excel'
}, {
  slug: 'pipedrive',
  name: 'Pipedrive'
}, {
  slug: 'segment',
  name: 'Segment'
}, {
  slug: 'amplitude',
  name: 'Amplitude'
}, {
  slug: 'mixpanel',
  name: 'Mixpanel'
}, {
  slug: 'datadog',
  name: 'Datadog'
}, {
  slug: 'sentry',
  name: 'Sentry'
}, {
  slug: 'pagerduty',
  name: 'PagerDuty'
}, {
  slug: 'twilio',
  name: 'Twilio'
}, {
  slug: 'sendgrid',
  name: 'SendGrid'
}, {
  slug: 'quickbooks',
  name: 'QuickBooks'
}, {
  slug: 'xero',
  name: 'Xero'
}, {
  slug: 'docusign',
  name: 'DocuSign'
}, {
  slug: 'webflow',
  name: 'Webflow',
  light: true
}, {
  slug: 'contentful',
  name: 'Contentful'
}, {
  slug: 'wordpress',
  name: 'WordPress'
}, {
  slug: 'discord',
  name: 'Discord'
}, {
  slug: 'gong',
  name: 'Gong'
}, {
  slug: 'snowflake',
  name: 'Snowflake'
}, {
  slug: 'looker',
  name: 'Looker'
}, {
  slug: 'tableau',
  name: 'Tableau'
}, {
  slug: 'box',
  name: 'Box'
}, {
  slug: 'okta',
  name: 'Okta'
}, {
  slug: 'aws',
  name: 'AWS',
  light: true
}, {
  slug: 'googlecloud',
  name: 'Google Cloud'
}, {
  slug: 'microsoftazure',
  name: 'Azure'
}, {
  slug: 'postman',
  name: 'Postman'
}, {
  slug: 'launchdarkly',
  name: 'LaunchDarkly'
}, {
  slug: 'pendo',
  name: 'Pendo'
}];
const MARQUEE_SET: {
  slug: string;
  name: string;
  light?: boolean;
}[] = [{
  slug: 'slack',
  name: 'Slack'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'airtable',
  name: 'Airtable'
}, {
  slug: 'notion',
  name: 'Notion',
  light: true
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'calendly',
  name: 'Calendly'
}, {
  slug: 'zoom',
  name: 'Zoom'
}, {
  slug: 'intercom',
  name: 'Intercom'
}, {
  slug: 'dropbox',
  name: 'Dropbox'
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'stripe',
  name: 'Stripe'
}];
function IntegrationsModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [tool, setTool] = useState('');
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const filtered = query.trim() ? ALL_INTEGRATION_SLUGS.filter(t => t.name.toLowerCase().includes(query.trim().toLowerCase())) : ALL_INTEGRATION_SLUGS;
  return <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} data-id="element-1288">
      <div className="absolute inset-0 bg-[#1A1410]/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-1289" />

      <motion.div role="dialog" aria-modal="true" aria-label="All integrations" className="relative w-full max-w-3xl max-h-[88vh] flex flex-col rounded-3xl bg-[#FAF8F4] text-[#2B211A] shadow-2xl shadow-black/40 overflow-hidden" initial={{
      opacity: 0,
      scale: 0.96,
      y: 16
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.96,
      y: 16
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 28
    }} data-id="element-1290">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/5 hover:bg-black/10 text-[#2B211A] transition-colors" data-id="element-1291">
          <X size={18} data-id="element-1292" />
        </button>

        <div className="px-6 md:px-10 pt-8 md:pt-10 pb-5" data-id="element-1293">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase text-[#EA580C] mb-3 [letter-spacing:5px]" data-id="element-1294">
            <Sparkles size={13} data-id="element-1295" />
            Connections
          </div>
          <h2 className="text-2xl md:text-3xl font-title font-bold tracking-tight leading-tight mb-2" data-id="element-1296">
            Honestly? There's nothing to see.
          </h2>
          <p className="text-sm md:text-base text-[#6B5D52] leading-relaxed max-w-xl" data-id="element-1297">
            We connect to everything. But sure — browse the thousands we've
            already wired up. Yours not here? Name it, and it's live in minutes.
          </p>
        </div>

        <div className="px-6 md:px-10 pb-4" data-id="element-1298">
          <div className="relative" data-id="element-1299">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9C8E82]" data-id="element-1300" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search thousands of connections…" className="w-full pl-11 pr-4 py-3 rounded-xl bg-white border border-[#E7E0D7] text-[#2B211A] placeholder:text-[#9C8E82] focus:outline-none focus:ring-2 focus:ring-[#EA580C]/30 focus:border-[#EA580C]/40 transition" data-id="element-1301" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 md:px-10 pb-4" data-id="element-1302">
          {filtered.length > 0 ? <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3" data-id="element-1303">
              {filtered.map((t, i) => <div key={`${t.slug}-${i}`} className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl bg-white border border-[#EFE9E1] hover:border-[#EA580C]/40 hover:shadow-sm transition-all" data-id="element-1304">
                  <div className="flex items-center justify-center w-9 h-9" data-id="element-1305">
                    <img src={`https://cdn.simpleicons.org/${t.slug}${t.light ? '/2B211A' : ''}`} alt={t.name} className="max-w-full max-h-full object-contain" loading="lazy" onError={e => {
                const img = e.currentTarget as HTMLImageElement;
                if (!img.dataset.fallback) {
                  img.dataset.fallback = '1';
                  img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${t.slug}.svg`;
                } else {
                  img.style.visibility = 'hidden';
                }
              }} data-id="element-1306" />
                  </div>
                  <span className="text-[11px] font-medium text-[#6B5D52] text-center leading-tight truncate w-full" data-id="element-1307">
                    {t.name}
                  </span>
                </div>)}
            </div> : <div className="py-10 text-center" data-id="element-1308">
              <p className="text-sm text-[#6B5D52]" data-id="element-1309">
                No match for "{query}" — but that's no problem.
              </p>
            </div>}
        </div>

        <div className="border-t border-[#EFE9E1] bg-[#F4F0EA] px-6 md:px-10 py-5" data-id="element-1310">
          {submitted ? <p className="text-sm font-medium text-[#2B211A]" data-id="element-1311">
              Got it — we'll get{' '}
              <span className="text-[#EA580C] font-semibold" data-id="element-1312">
                {tool.trim() || 'your tool'}
              </span>{' '}
              set up in a few minutes. 🎉
            </p> : <form onSubmit={e => {
          e.preventDefault();
          setSubmitted(true);
        }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-1313">
              <label className="text-sm font-medium text-[#2B211A] sm:whitespace-nowrap" data-id="element-1314">
                Can't find your tool?
              </label>
              <div className="flex-1 flex items-stretch gap-2" data-id="element-1315">
                <input type="text" value={tool} onChange={e => setTool(e.target.value)} placeholder="Name your tool…" className="flex-1 px-4 py-2.5 rounded-xl bg-white border border-[#E7E0D7] text-[#2B211A] placeholder:text-[#9C8E82] focus:outline-none focus:ring-2 focus:ring-[#EA580C]/30 focus:border-[#EA580C]/40 transition" data-id="element-1316" />
                <button type="submit" className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#EA580C] hover:bg-[#d24e09] text-white font-semibold transition-colors whitespace-nowrap" data-id="element-1317">
                  Send it
                  <Send size={16} data-id="element-1318" />
                </button>
              </div>
            </form>}
        </div>
      </motion.div>
    </motion.div>;
}
function LogoTile({
  logo
}: {
  logo: {
    slug: string;
    name: string;
    light?: boolean;
  };
}) {
  return <div className="shrink-0 flex items-center justify-center w-16 h-16 md:w-[100px] md:h-[100px] rounded-xl md:rounded-2xl bg-[#1A1410] border border-white/[0.07] shadow-lg shadow-black/30" title={logo.name} data-id="element-1319">
      <img src={`https://cdn.simpleicons.org/${logo.slug}${logo.light ? '/e8e6e3' : ''}`} alt={logo.name} className="w-7 h-7 md:w-11 md:h-11 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${logo.slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-1320" />
    </div>;
}
function LogoMarquee({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  return <div className="relative w-full overflow-hidden" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)'
  }} data-id="element-1321">
      <motion.div className="flex gap-3 md:gap-5 w-max" animate={reducedMotion ? undefined : {
      x: ['-50%', '0%']
    }} transition={reducedMotion ? undefined : {
      duration: 40,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-1322">
        {[...MARQUEE_SET, ...MARQUEE_SET].map((logo, i) => <LogoTile key={`${logo.slug}-${i}`} logo={logo} data-id="element-1323" />)}
      </motion.div>
    </div>;
}
const STEPS = [{
  n: 1,
  icon: Link2Icon,
  title: 'Connect your tools',
  body: 'Get set up in minutes — no APIs to wrangle, no engineers required.'
}, {
  n: 2,
  icon: PencilIcon,
  title: 'Describe the task',
  body: 'Say what you want in plain English — the outcome, not the steps.'
}, {
  n: 3,
  icon: CheckIcon,
  title: 'Giga gets it done',
  body: "Giga picks the tools, does the work, and pings you the moment it's done."
}];
type PromptSegment = {
  type: 'text';
  content: string;
} | {
  type: 'chip';
  slug: string;
  name: string;
  light?: boolean;
} | {
  type: 'mention';
  name: string;
};
type PromptAction = {
  slug: string;
  name: string;
  label: string;
};
type Workflow = {
  segments: PromptSegment[];
  actions: PromptAction[];
};
// Different workflows the section rotates through, so it's clear Giga handles
// many use cases — not just one scripted demo.
const WORKFLOWS: Workflow[] = [{
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' pull our latest closed deals from '
  }, {
    type: 'chip',
    slug: 'hubspot',
    name: 'HubSpot'
  }, {
    type: 'text',
    content: ' draft the onboarding doc in '
  }, {
    type: 'chip',
    slug: 'notion',
    name: 'Notion',
    light: true
  }, {
    type: 'text',
    content: ' and invite each client in '
  }, {
    type: 'chip',
    slug: 'slack',
    name: 'Slack'
  }],
  actions: [{
    slug: 'hubspot',
    name: 'HubSpot',
    label: 'Pulled the closed deals from HubSpot'
  }, {
    slug: 'notion',
    name: 'Notion',
    label: 'Spun up the onboarding doc in Notion'
  }, {
    slug: 'slack',
    name: 'Slack',
    label: 'Invited the client in Slack'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: ' review my '
  }, {
    type: 'chip',
    slug: 'zoom',
    name: 'Zoom'
  }, {
    type: 'text',
    content: ' call from this morning, log the action items in '
  }, {
    type: 'chip',
    slug: 'linear',
    name: 'Linear'
  }, {
    type: 'text',
    content: ' and email the recap from '
  }, {
    type: 'chip',
    slug: 'gmail',
    name: 'Gmail'
  }],
  actions: [{
    slug: 'zoom',
    name: 'Zoom',
    label: 'Pulled the notes from your Zoom call'
  }, {
    slug: 'linear',
    name: 'Linear',
    label: 'Logged the action items in Linear'
  }, {
    slug: 'gmail',
    name: 'Gmail',
    label: 'Sent the recap from Gmail'
  }]
}, {
  segments: [{
    type: 'mention',
    name: 'Giga'
  }, {
    type: 'text',
    content: " pull this week's payments from "
  }, {
    type: 'chip',
    slug: 'stripe',
    name: 'Stripe'
  }, {
    type: 'text',
    content: ' post the wins to '
  }, {
    type: 'chip',
    slug: 'slack',
    name: 'Slack'
  }, {
    type: 'text',
    content: ' and update each record in '
  }, {
    type: 'chip',
    slug: 'salesforce',
    name: 'Salesforce'
  }],
  actions: [{
    slug: 'stripe',
    name: 'Stripe',
    label: "Pulled this week's payments from Stripe"
  }, {
    slug: 'slack',
    name: 'Slack',
    label: 'Posted the win to Slack'
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    label: 'Updated the record in Salesforce'
  }]
}];
function ToolChip({
  slug,
  name,
  light
}: {
  slug: string;
  name: string;
  light?: boolean;
}) {
  return <motion.span initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-[#F4F0EA] border border-neutral-200 text-sm font-medium text-[#2B211A] mx-1 align-middle shadow-sm overflow-hidden" data-id="element-1324">
      <img src={`https://cdn.simpleicons.org/${slug}`} alt={name} className="relative w-3.5 h-3.5 object-contain" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-1325" />
      <span className="relative" data-id="element-1326">{name}</span>
    </motion.span>;
}
// Slack-style @mention token for Giga — renders like a clickable user mention
// inside the message (highlighted pill with an @ prefix).
function GigaMention({
  name
}: {
  name: string;
}) {
  return <motion.span initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.2,
    ease: 'easeOut'
  }} role="link" className="inline rounded-[3px] px-1 text-[#EA580C] font-semibold cursor-pointer transition-colors hover:underline bg-[#EA580C]/12" data-id="element-1327">
      @{name}
    </motion.span>;
}
// Total "type units" in a prompt: every text character counts as one unit and
// every chip counts as one unit, so we can reveal the message progressively.
function promptUnitCount(segments: PromptSegment[]) {
  return segments.reduce((sum, seg) => sum + (seg.type === 'text' ? seg.content.length : 1), 0);
}
// Render the portion of a prompt that has been "typed" so far. Text reveals
// character-by-character; chips pop in as a whole the moment they're reached.
function renderTyped(segments: PromptSegment[], revealed: number): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.type === 'text') {
      const shown = Math.max(0, Math.min(seg.content.length, revealed - cursor));
      if (shown > 0) {
        out.push(<span key={i} data-id="element-1328">{seg.content.slice(0, shown)}</span>);
      }
      cursor += seg.content.length;
    } else if (seg.type === 'mention') {
      if (revealed > cursor) {
        out.push(<GigaMention key={i} name={seg.name} data-id="element-1329" />);
      }
      cursor += 1;
    } else {
      if (revealed > cursor) {
        out.push(<ToolChip key={i} slug={seg.slug} name={seg.name} light={seg.light} data-id="element-1330" />);
      }
      cursor += 1;
    }
    if (cursor >= revealed) break;
  }
  return out;
}
function TypewriterInputCard({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  const [workflowIndex, setWorkflowIndex] = useState(0);
  const workflow = WORKFLOWS[workflowIndex];
  const totalUnits = promptUnitCount(workflow.segments);
  // Reveal the prompt one "type unit" at a time, then pause and cycle to the
  // next workflow so visitors see multiple use cases — no send/action loop.
  const [revealed, setRevealed] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef);
  useEffect(() => {
    if (reducedMotion) {
      setRevealed(totalUnits);
      return;
    }
    if (!inView) return;
    if (revealed < totalUnits) {
      const atChip = (() => {
        let cursor = 0;
        for (const seg of workflow.segments) {
          const len = seg.type === 'text' ? seg.content.length : 1;
          if (revealed >= cursor && revealed < cursor + len) {
            return seg.type === 'chip';
          }
          cursor += len;
        }
        return false;
      })();
      const id = setTimeout(() => setRevealed(r => r + 1), atChip ? 140 : 22);
      return () => clearTimeout(id);
    } else {
      // Fully typed — hold for a beat, then advance to the next workflow.
      const id = setTimeout(() => {
        setWorkflowIndex(i => (i + 1) % WORKFLOWS.length);
        setRevealed(0);
      }, 1800);
      return () => clearTimeout(id);
    }
  }, [reducedMotion, inView, revealed, totalUnits, workflow.segments, workflowIndex]);
  const isTyping = !reducedMotion && revealed < totalUnits;
  return <motion.div ref={containerRef} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6,
    delay: 0
  }} className="relative bg-white rounded-[24px] border border-neutral-200/80 shadow-2xl p-5 pb-14 md:p-7 md:pb-16 max-w-[960px] mx-auto" data-id="element-1331">
      {/* Fixed-height text area so the card never grows/shrinks as the prompt
                                                                                                                   types in or swaps workflows. The first lines run full width; only the
                                                                                                                   bottom-right corner (where the send button sits) is carved out via a
                                                                                                                   floated shape-outside spacer, so the last line wraps before the arrow
                                                                                                                   circle instead of sliding under it. */}
      <div className="text-left text-sm md:text-xl font-medium text-[#2B211A] leading-[2.1] md:leading-[2] h-[92px] md:h-[88px] overflow-hidden" data-id="element-1332">
        <span aria-hidden="true" className="float-right h-full w-[4.25rem] md:w-[5.25rem]" style={{
        shapeOutside: 'inset(calc(100% - 3.25rem) 0 0 0)'
      }} data-id="element-1333" />
        {renderTyped(workflow.segments, revealed)}
        {isTyping && <span aria-hidden="true" className="inline-block w-[2px] h-[1em] bg-[#EA580C] align-middle ml-0.5 animate-pulse" data-id="element-1334" />}
      </div>
      <div className="absolute bottom-5 right-5 md:bottom-8 md:right-8 w-10 h-10 md:w-12 md:h-12 shrink-0 rounded-full bg-[#EA580C] text-white flex items-center justify-center shadow-lg" data-id="element-1335">
        <ArrowRight className="w-[18px] h-[18px] md:w-5 md:h-5" strokeWidth={2.5} data-id="element-1336" />
      </div>
    </motion.div>;
}
// What "Giga gets it done" actually produces: a notification-style result card
// showing the finished output, so the section's "action out" promise is concrete
// instead of abstract. Mirrors the second workflow (Zoom → Linear → Gmail).
const RESULT_ACTIONS: {
  slug: string;
  light?: boolean;
  label: string;
}[] = [{
  slug: 'zoom',
  label: 'Pulled the action items from your Zoom call'
}, {
  slug: 'linear',
  label: 'Logged 4 follow-up tasks in Linear'
}, {
  slug: 'gmail',
  label: 'Sent the recap email from Gmail'
}];
function ResultActionIcon({
  slug,
  light
}: {
  slug: string;
  light?: boolean;
}) {
  return <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-white shrink-0" data-id="element-1337">
      <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className="w-4 h-4 object-contain" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.simpleicons.org/${slug}${light ? '/2B211A' : ''}`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-1338" />
    </span>;
}
// Single tool lockup (icon + name) for the hero "Giga plugs into your world"
// carousel. Icons are rendered MONOCHROME — every logo is forced to one solid
// tone (dark on the light hero, light on dark) so the row reads clean instead
// of a rainbow of brand colors.
function TrustToolLogo({
  slug,
  name,
  light,
  isHero
}: {
  slug: string;
  name: string;
  light?: boolean;
  isHero?: boolean;
}) {
  // Force a single fill color via simpleicons' /HEX param for true monochrome.
  const tone = isHero ? '2B211A' : 'e8e6e3';
  return <div className="flex items-center gap-2.5 shrink-0" data-id="element-1339">
      <img src={`https://cdn.simpleicons.org/${slug}/${tone}`} alt="" aria-hidden="true" loading="lazy" className="h-6 w-6 object-contain opacity-80" onError={e => {
      ;
      (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
    }} data-id="element-1340" />
      <span className={`text-sm font-semibold whitespace-nowrap ${isHero ? 'text-neutral-600' : 'text-white/80'}`} data-id="element-1341">
        {name}
      </span>
    </div>;
}
function ResultNotification({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  return <div className="mt-16 md:mt-24" data-id="element-1342">
      {/* Small lead-in connecting the steps to the payoff */}
      <motion.p initial={reducedMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.5
    }} className="text-xs font-semibold tracking-[4px] uppercase text-neutral-400 mb-6" data-id="element-1343">
        And here's what shows up done
      </motion.p>

      <motion.div initial={reducedMotion ? {
      opacity: 1,
      y: 0,
      scale: 1
    } : {
      opacity: 0,
      y: 24,
      scale: 0.98
    }} whileInView={{
      opacity: 1,
      y: 0,
      scale: 1
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      type: 'spring',
      stiffness: 220,
      damping: 24
    }} className="relative text-left max-w-[520px] mx-auto rounded-[22px] bg-white/[0.06] backdrop-blur-sm border border-white/10 shadow-2xl shadow-black/40 overflow-hidden" data-id="element-1344">
        {/* Header — looks like a real notification from Giga */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10" data-id="element-1345">
          <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#EA580C] shrink-0" data-id="element-1346">
            <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" aria-hidden="true" className="w-4 h-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-1347" />
          </span>
          <div className="min-w-0" data-id="element-1348">
            <p className="text-sm font-semibold text-white leading-tight" data-id="element-1349">
              Giga
            </p>
            <p className="text-[11px] text-neutral-400 leading-tight" data-id="element-1350">
              Co-pilot · just now
            </p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#EA580C]/15 border border-[#EA580C]/30 text-[11px] font-bold text-[#F8B98C] uppercase tracking-wide shrink-0" data-id="element-1351">
            <Check size={12} strokeWidth={3} data-id="element-1352" />
            Done
          </span>
        </div>

        {/* Body */}
        <div className="px-5 py-4" data-id="element-1353">
          <p className="text-sm text-neutral-200 leading-relaxed mb-4" data-id="element-1354">
            <span className="font-semibold text-white" data-id="element-1355">Done.</span> Here's what
            I took off your plate — ready for you to review:
          </p>

          <ul className="flex flex-col gap-2.5" data-id="element-1356">
            {RESULT_ACTIONS.map((action, i) => <motion.li key={action.slug} initial={reducedMotion ? {
            opacity: 1,
            x: 0
          } : {
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: reducedMotion ? 0 : 0.25 + i * 0.18
          }} className="flex items-center gap-3" data-id="element-1357">
                <ResultActionIcon slug={action.slug} light={action.light} data-id="element-1358" />
                <span className="flex-1 text-sm text-neutral-300 leading-snug" data-id="element-1359">
                  {action.label}
                </span>
                <motion.span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C] text-white shrink-0" initial={reducedMotion ? {
              scale: 1
            } : {
              scale: 0
            }} whileInView={{
              scale: 1
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 18,
              delay: reducedMotion ? 0 : 0.4 + i * 0.18
            }} data-id="element-1360">
                  <Check size={12} strokeWidth={3} data-id="element-1361" />
                </motion.span>
              </motion.li>)}
          </ul>
        </div>
      </motion.div>
    </div>;
}
export function HowItWorksConnections({
  mode = 'full'
}: {
  // 'full'  → everything (hero chat + steps + connections) — used at /future
  // 'hero'  → just the "Ask Giga anything" headline + chat composer
  // 'how'   → the "How it works" 3-step explainer + result + connections
  mode?: 'full' | 'hero' | 'how';
}) {
  const copy = useCopy();
  const reducedMotion = useReducedMotion();
  const [modalOpen, setModalOpen] = useState(false);
  const isHero = mode === 'hero';
  const showHero = mode === 'hero' || mode === 'full';
  const showSteps = mode === 'how' || mode === 'full';
  const showConnections = mode === 'how' || mode === 'full';
  return <section className={`relative w-full overflow-hidden ${isHero ? 'bg-[#FAF8F4] text-[#2B211A] pt-32 pb-10 md:pt-44 md:pb-12' : 'bg-[#2B211A] text-white py-16 md:py-40'}`} data-id="element-1362">
      {/* Image background with warm radial glows (only for dark mode) */}
      {!isHero && <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/nrRqqKEDJg9H9wijnoz2Si/How_it_works_(4).png')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }} data-id="element-1363" />}

      {/* Light mode background elements */}
      {isHero && <>
          <div className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-1364" />
          <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply pointer-events-none z-0" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-1365"></div>
        </>}

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1366">
        {/* HEADER */}
        <div className={`text-center ${isHero ? '' : 'mb-16 md:mb-20'}`} data-id="element-1367">
          <span className={`block text-xs font-semibold tracking-[5px] uppercase mb-5 md:mb-6 ${isHero ? 'text-[#EA580C]' : 'text-neutral-400'}`} data-id="element-1368">
            {'AI Brain for your business '}
          </span>

          {/* Hero headline + chat composer */}
          {showHero && <h2 className={cn(`font-title font-medium tracking-tight leading-[1.05] max-w-4xl mx-auto ${isHero ? 'text-[#2B211A] text-4xl sm:text-6xl md:text-7xl mb-10 md:mb-14' : 'text-white text-3xl md:text-5xl mb-12 md:mb-16'}`, 'mt-[0px] mb-[0px]', 'mt-[0px] mb-[0px]')} data-id="element-1369">
              {copy.howItWorksConnections.headline1}
            </h2>}
          {showHero && isHero && <p className="text-base md:text-lg text-[#6B5D52] leading-relaxed max-w-3xl mx-auto -mt-12 md:-mt-20 mb-12 md:mb-16" data-id="element-1370">
              @mention Giga in any channel — it pulls answers from every tool
              you've connected.
            </p>}
          {showHero && <div data-id="element-1371">
              <TypewriterInputCard reducedMotion={!!reducedMotion} data-id="element-1372" />

              {/* CTA */}
              <div className="mt-10 md:mt-12 flex justify-center" data-id="element-1373">
                <a href="/signup" className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#EA580C] hover:bg-[#d24e09] text-white font-bold text-lg shadow-lg shadow-[#EA580C]/20 transition-all hover:scale-105" data-id="element-1374">
                  Get started
                  <span className="flex items-center gap-2 pl-3 border-l border-white/25" data-id="element-1375">
                    <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/slack.png" alt="Slack" className="h-5 w-5 object-contain" onError={e => {
                  const img = e.currentTarget as HTMLImageElement;
                  if (!img.dataset.fallback) {
                    img.dataset.fallback = '1';
                    img.src = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg';
                  } else {
                    img.style.visibility = 'hidden';
                  }
                }} data-id="element-1376" />
                    <img src="https://cdn.simpleicons.org/microsoftteams/ffffff" alt="Microsoft Teams" className="h-5 w-5 object-contain" onError={e => {
                  const img = e.currentTarget as HTMLImageElement;
                  if (!img.dataset.fallback) {
                    img.dataset.fallback = '1';
                    img.src = 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg';
                  } else {
                    img.style.visibility = 'hidden';
                  }
                }} data-id="element-1377" />
                  </span>
                </a>
              </div>

              {/* Connects-to badge — eyebrow label over an auto-scrolling
               carousel of the tools Giga plugs into. */}
              <div className="mt-12 md:mt-16 flex flex-col items-center" data-id="element-1378">
                <p className={`text-[10px] md:text-xs font-semibold tracking-[4px] uppercase mb-8 ${isHero ? 'text-neutral-400' : 'text-neutral-500'}`} data-id="element-1379">
                  Giga plugs into your world
                </p>
                {/* Auto-scrolling logo carousel — integration tool logos.
                 Two identical halves animate x: 0 → -50% so the track is
                 always full edge-to-edge and never visibly resets. */}
                <div className="relative w-full overflow-hidden" style={{
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
              maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)'
            }} data-id="element-1380">
                  <motion.div className="flex w-max items-center gap-x-8 md:gap-x-12" animate={reducedMotion ? undefined : {
                x: ['0%', '-50%']
              }} transition={reducedMotion ? undefined : {
                duration: 55,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-1381">
                    {[0, 1].map(dup => <div key={dup} className="flex items-center gap-x-8 md:gap-x-12" aria-hidden={dup === 1} data-id="element-1382">
                        {TRUST_TOOL_LOGOS.map(tool => <TrustToolLogo key={`${dup}-${tool.slug}`} slug={tool.slug} name={tool.name} light={tool.light} isHero={isHero} data-id="element-1383" />)}
                      </div>)}
                  </motion.div>
                </div>
              </div>

              {/* Closing divider line beneath the trust badge */}
              <div className={`mt-12 md:mt-16 w-full border-t ${isHero ? 'border-neutral-200/60' : 'border-white/10'}`} data-id="element-1384" />
            </div>}

          {/* "How it works" explainer headline (only when the steps lead this
           section, i.e. it is not also the chat hero) */}
          {showSteps && !showHero && <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight text-white leading-[1.1] mb-12 md:mb-16 max-w-4xl mx-auto" data-id="element-1385">
              Connect, describe, done.
            </h2>}

          {/* 3-Step Stepper — sequenced left→right, the connecting line glows
           as it travels and each step lights up the moment it's reached. */}
          {showSteps && <div className="relative" data-id="element-1386">
              {/* Desktop connecting line */}
              <div className="hidden md:block absolute top-[21px] left-[calc(16.66%-16px)] w-[calc(66.66%+32px)] h-[1px] z-0" data-id="element-1387">
                {/* Static dashed base */}
                <div className="absolute inset-0 border-t border-dashed border-white/25" data-id="element-1388" />
                {/* Glowing line that travels left → right */}
                <motion.div aria-hidden="true" className="absolute inset-y-0 left-0 origin-left" style={{
              width: '100%'
            }} initial={reducedMotion ? {
              scaleX: 1
            } : {
              scaleX: 0
            }} whileInView={{
              scaleX: reducedMotion ? 1 : [0, 0.5, 0.5, 1]
            }} viewport={{
              once: true,
              margin: '-120px'
            }} transition={{
              duration: reducedMotion ? 0 : 2.2,
              times: [0, 0.4, 0.6, 1],
              ease: ['easeInOut', 'linear', 'easeInOut']
            }} data-id="element-1389">
                  <div className="absolute inset-0 h-[2px] -mt-px bg-[#EA580C] shadow-[0_0_12px_2px_rgba(234,88,12,0.6)]" data-id="element-1390" />
                </motion.div>
              </div>

              {/* Steps stack vertically on mobile and lay out as a 3-up grid on
               desktop (with the glowing connecting line). */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-y-12 md:gap-y-0 md:gap-x-12 lg:gap-x-20 relative z-10" data-id="element-1391">
                {STEPS.map((step, i) => {
              // The glow front sweeps across over 2.2s, pausing at the middle step.
              // Light up each step the moment the line reaches it.
              const lightDelay = reducedMotion ? 0 : i === 0 ? 0.15 : i === 1 ? 0.88 : 2.2;
              const isLast = i === STEPS.length - 1;
              return <div key={step.n} className="relative flex flex-col items-center" data-id="element-1392">
                      {/* Badge — pops in like a bubble as the line reaches it */}
                      <motion.div className="relative mb-5 md:mb-6 z-10" initial={reducedMotion ? {
                  opacity: 1,
                  scale: 1
                } : {
                  opacity: 0,
                  scale: 0.5
                }} whileInView={{
                  opacity: 1,
                  scale: 1
                }} viewport={{
                  once: true,
                  margin: '-100px'
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 20,
                  delay: lightDelay
                }} data-id="element-1393">
                        {/* Bloom of light that fires when the line arrives */}
                        <motion.div aria-hidden="true" className="absolute inset-0 rounded-full bg-[#EA580C] blur-xl" initial={reducedMotion ? {
                    opacity: 0.25,
                    scale: 1.3
                  } : {
                    opacity: 0,
                    scale: 0.6
                  }} whileInView={{
                    opacity: [0, 0.55, 0.25],
                    scale: [0.6, 1.6, 1.3]
                  }} viewport={{
                    once: true,
                    margin: '-100px'
                  }} transition={{
                    duration: 0.8,
                    delay: lightDelay,
                    ease: 'easeOut'
                  }} data-id="element-1394" />
                        {/* Dark circular badge with orange border and step number */}
                        <div className="relative w-10 h-10 md:w-11 md:h-11 shrink-0 rounded-full border border-[#EA580C] bg-[#2B211A] flex items-center justify-center" data-id="element-1395">
                          <span className="font-title text-base md:text-lg font-normal text-[#EA580C] leading-none" data-id="element-1396">
                            {step.n}
                          </span>
                        </div>
                      </motion.div>

                      {/* Mobile connector — drops straight down from this step's
                       numbered circle to its OWN headline (circle → headline),
                       so each step reads as a self-contained "1 → its title".
                       Hidden on desktop, which uses the horizontal line above. */}
                      <div className="md:hidden relative h-7 w-[1px] -mt-3 mb-1 z-0" data-id="element-1397">
                        {/* Static dashed base */}
                        <div className="absolute inset-0 border-l border-dashed border-white/25" data-id="element-1398" />
                        {/* Glowing segment that fills top → bottom */}
                        <motion.div aria-hidden="true" className="absolute inset-x-0 top-0 origin-top" style={{
                    height: '100%'
                  }} initial={reducedMotion ? {
                    scaleY: 1
                  } : {
                    scaleY: 0
                  }} whileInView={{
                    scaleY: 1
                  }} viewport={{
                    once: true,
                    margin: '-100px'
                  }} transition={{
                    duration: reducedMotion ? 0 : 0.45,
                    delay: lightDelay + 0.1,
                    ease: 'easeInOut'
                  }} data-id="element-1399">
                          <div className="absolute inset-0 w-[2px] -ml-px bg-[#EA580C] shadow-[0_0_12px_2px_rgba(234,88,12,0.6)]" data-id="element-1400" />
                        </motion.div>
                      </div>

                      {/* Title + body — centered. */}
                      <motion.div className="text-center max-w-[280px]" initial={reducedMotion ? {
                  opacity: 1,
                  y: 0
                } : {
                  opacity: 0,
                  y: 15
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true,
                  margin: '-100px'
                }} transition={{
                  duration: 0.5,
                  delay: lightDelay + 0.15
                }} data-id="element-1401">
                        <h3 className="font-title font-medium text-xl md:text-2xl text-white mb-2.5 md:mb-3.5 tracking-tight leading-tight" data-id="element-1402">
                          {step.title}
                        </h3>
                        <p className="text-sm md:text-base text-neutral-400 leading-relaxed" data-id="element-1403">
                          {step.body}
                        </p>
                      </motion.div>
                    </div>;
            })}
              </div>
            </div>}

          {/* Payoff — what "Giga gets it done" actually delivers */}
          {showSteps && <ResultNotification reducedMotion={!!reducedMotion} data-id="element-1404" />}
        </div>
      </div>

      {/* CONNECTIONS Section */}
      {showConnections && <div className="relative z-10 w-full mt-16 md:mt-32" data-id="element-1405">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 mb-10 md:mb-20" data-id="element-1406">
            <div className="text-center" data-id="element-1407">
              <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-1408">
                Connections
              </span>
              <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-white mb-5 md:mb-6" data-id="element-1409">
                {copy.howItWorksConnections.headline2}
              </h2>
              <p className="text-base md:text-xl text-neutral-300 leading-relaxed max-w-3xl mx-auto" data-id="element-1410">
                Connect <span className="font-bold text-white" data-id="element-1411">ANY</span> tool
                you use in a few clicks — most are live in seconds. Giga pulls
                context between them so there's no more switching tabs, copying
                data, or gluing information together yourself.
              </p>
            </div>
          </div>

          {/* Full-bleed marquee */}
          <div className="mb-10 md:mb-16" data-id="element-1412">
            <LogoMarquee reducedMotion={!!reducedMotion} data-id="element-1413" />
          </div>

          {/* CTA */}
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 flex justify-center" data-id="element-1414">
            <button onClick={() => setModalOpen(true)} className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white hover:bg-neutral-50 text-[#2B211A] font-bold shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5" data-id="element-1415">
              See all connections
              <ArrowRight size={18} data-id="element-1416" />
            </button>
          </div>
        </div>}

      <AnimatePresence data-id="element-1417">
        {modalOpen && <IntegrationsModal onClose={() => setModalOpen(false)} data-id="element-1418" />}
      </AnimatePresence>
    </section>;
}